import React, { useState, useEffect, useCallback } from "react";
import useApi from "@/hooks/useApi";
import useToastAlert from "@/hooks/useToastAlert";
import useSubscriptions from "@/hooks/useSubscriptions";
import {
  Severity,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  PrimaryBlueButton,
  SecondaryBlueButton
} from "@velaro/velaro-component-lib";

interface AllowlistEntry {
  id: number;
  ipOrRange: string;
  type: string;
  label: string | null;
  isActive: boolean;
  expiresAt: string | null;
  isEmergencyBypass: boolean;
  createdBy: string;
  createdAt: string;
  isExpired: boolean;
}

interface AuditEntry {
  id: number;
  action: string;
  performedBy: string;
  performedAt: string;
  detail: string;
  ipAddress: string;
}

type InnerTab = "rules" | "audit";

export default function AgentIpAllowlistPanel() {
  const { subscription } = useSubscriptions();
  const [tab, setTab] = useState<InnerTab>("rules");

  const hasRestriction = subscription?.enableAgentIpRestriction ?? false;

  if (!hasRestriction) {
    return (
      <div className="p-6 max-w-3xl">
        <h2 className="text-xl font-semibold mb-4">Agent Login IP Restriction</h2>

        {/* How it works dropdown */}
        <HowItWorksDropdown />

        <div className="bg-amber-50 border border-amber-200 rounded-lg p-5 mt-4">
          <p className="font-medium text-amber-800 mb-2">Not included in your current plan</p>
          <p className="text-sm text-amber-700">
            Agent IP Restriction prevents your agents from logging in from unauthorized networks.
            Only IPs you explicitly allow (corporate office, VPN range, etc.) will be permitted.
            Contact sales@velaro.com to add this to your plan.
          </p>
        </div>
      </div>
    );
  }

  const tabClass = (t: InnerTab) =>
    `px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
      tab === t
        ? "border-primary text-primary-active"
        : "border-transparent text-slate-500 hover:text-slate-700"
    }`;

  return (
    <div className="p-6 max-w-4xl">
      <div className="mb-4">
        <h2 className="text-xl font-semibold">Agent Login IP Restriction</h2>
        <p className="text-sm text-slate-500 mt-1">
          Restrict which IP addresses and networks your agents can log in from.
        </p>
      </div>

      <HowItWorksDropdown />

      <div className="flex border-b border-slate-200 my-4">
        <button className={tabClass("rules")} onClick={() => setTab("rules")}>Allowed IPs</button>
        <button className={tabClass("audit")} onClick={() => setTab("audit")}>Audit Log</button>
      </div>

      {tab === "rules" && <AllowlistRules />}
      {tab === "audit" && <AllowlistAuditLog />}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// How It Works dropdown explanation
// ─────────────────────────────────────────────────────────────────

function HowItWorksDropdown() {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-slate-200 rounded-lg mb-2">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-4 text-left hover:bg-slate-50 transition"
        aria-expanded={open}
      >
        <span className="text-sm font-medium text-slate-700">How Agent IP Restriction works</span>
        <svg className={`w-4 h-4 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`}
          fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <div className="border-t border-slate-200 p-4 text-sm text-slate-600 space-y-3">
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <p className="font-medium text-green-800 mb-1">✓ When an allowed IP list is configured:</p>
              <ul className="text-green-700 text-xs space-y-1 list-disc list-inside">
                <li>Agents can ONLY log in from listed IP ranges</li>
                <li>Login from any other IP is blocked immediately</li>
                <li>Every attempt (pass/fail) is logged with IP address</li>
              </ul>
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
              <p className="font-medium text-slate-700 mb-1">If no entries are configured:</p>
              <ul className="text-slate-600 text-xs space-y-1 list-disc list-inside">
                <li>Restriction is effectively disabled</li>
                <li>Agents can log in from anywhere (normal behavior)</li>
                <li>Add at least one entry to activate restriction</li>
              </ul>
            </div>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
            <p className="font-semibold text-amber-800 text-xs mb-1">⚠ Before you add your first entry:</p>
            <p className="text-amber-700 text-xs">
              Make sure you add your own IP or network first. Once any entry exists, anyone not on the list
              (including you) will be blocked. Use the Emergency Bypass option for temporary access
              during travel or remote work. Emergency bypasses auto-expire and generate extra audit events.
            </p>
          </div>
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs">
            <p className="font-medium text-slate-700 mb-1">Supported formats:</p>
            <ul className="text-slate-600 space-y-1 font-mono">
              <li>Single IP: <code>203.0.113.45</code></li>
              <li>CIDR range: <code>192.168.1.0/24</code> (all IPs in subnet)</li>
              <li>IP range: <code>10.0.0.1-10.0.0.100</code></li>
              <li>IPv6: <code>2001:db8::/32</code></li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Allowlist Rules
// ─────────────────────────────────────────────────────────────────

function AllowlistRules() {
  const api = useApi();
  const toastAlert = useToastAlert();
  const [entries, setEntries] = useState<AllowlistEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);

  const fetchEntries = useCallback(async () => {
    try {
      const resp = await api.messaging.get("AgentIpAllowlist");
      if (resp.ok) {
        const data = await resp.json();
        setEntries(Array.isArray(data) ? data : []);
      }
    } catch { /* graceful */ }
    finally { setLoading(false); }
  }, [api.messaging]);

  useEffect(() => { fetchEntries(); }, [fetchEntries]);

  const handleDelete = async (entry: AllowlistEntry) => {
    if (!window.confirm(`Remove "${entry.ipOrRange}" (${entry.label ?? "no label"}) from the allowlist? Agents using only this IP will be blocked immediately.`)) return;
    try {
      const resp = await api.messaging.delete(`AgentIpAllowlist/${entry.id}`);
      if (resp.ok) {
        toastAlert.displayToast(Severity.Success, "Entry removed from allowlist.");
        fetchEntries();
      } else {
        const err = await resp.json();
        toastAlert.displayToast(Severity.Error, err.error ?? "Failed to remove entry.");
      }
    } catch {
      toastAlert.displayToast(Severity.Error, "Failed to remove entry.");
    }
  };

  const handleToggle = async (entry: AllowlistEntry) => {
    try {
      const resp = await api.messaging.put(`AgentIpAllowlist/${entry.id}`, { isActive: !entry.isActive });
      if (resp.ok) {
        toastAlert.displayToast(Severity.Success, entry.isActive ? "Entry deactivated." : "Entry activated.");
        fetchEntries();
      }
    } catch {
      toastAlert.displayToast(Severity.Error, "Failed to update entry.");
    }
  };

  const typeBadge = (type: string) => {
    const colors: Record<string, string> = {
      single: "bg-slate-100 text-slate-600",
      cidr: "bg-blue-100 text-blue-700",
      range: "bg-purple-100 text-purple-700"
    };
    return <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${colors[type] ?? "bg-slate-100 text-slate-600"}`}>{type}</span>;
  };

  if (loading) return <div className="text-sm text-slate-400 py-8">Loading allowlist...</div>;

  const active = entries.filter(e => e.isActive && !e.isExpired).length;

  return (
    <>
      <div className="flex items-center justify-between mb-3">
        <div>
          <span className="text-sm text-slate-600">{active} active entr{active !== 1 ? "ies" : "y"}</span>
          {active === 0 && (
            <span className="ml-2 text-xs text-slate-400">(restriction disabled until at least one active entry exists)</span>
          )}
        </div>
        <button
          onClick={() => setShowAdd(true)}
          className="px-3 py-1.5 bg-primary text-white text-sm font-medium rounded-lg hover:bg-primary"
        >
          + Add IP / Range
        </button>
      </div>

      {entries.length === 0 ? (
        <div className="text-center py-8 text-sm text-slate-400">
          No allowed IPs configured. Add an entry to activate restriction.
        </div>
      ) : (
        <div className="overflow-x-auto border border-slate-200 rounded-lg">
          <table className="w-full text-left text-sm">
            <thead className="bg-slate-50 text-xs text-slate-500 uppercase">
              <tr>
                <th className="px-3 py-2">IP / Range</th>
                <th className="px-3 py-2">Type</th>
                <th className="px-3 py-2">Label</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Expires</th>
                <th className="px-3 py-2">Added By</th>
                <th className="px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {entries.map(entry => (
                <tr key={entry.id} className={`border-t border-slate-100 hover:bg-slate-50 ${entry.isExpired ? "opacity-50" : ""}`}>
                  <td className="px-3 py-2 font-mono text-xs">
                    {entry.ipOrRange}
                    {entry.isEmergencyBypass && (
                      <span className="ml-1 px-1 py-0.5 bg-orange-100 text-orange-700 text-[10px] rounded">BYPASS</span>
                    )}
                  </td>
                  <td className="px-3 py-2">{typeBadge(entry.type)}</td>
                  <td className="px-3 py-2 text-xs text-slate-500">{entry.label ?? "—"}</td>
                  <td className="px-3 py-2">
                    <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${
                      entry.isExpired ? "bg-slate-100 text-slate-400" :
                      entry.isActive ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500"
                    }`}>
                      {entry.isExpired ? "Expired" : entry.isActive ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-xs">{entry.expiresAt ? new Date(entry.expiresAt).toLocaleDateString() : "Never"}</td>
                  <td className="px-3 py-2 text-xs">{entry.createdBy}</td>
                  <td className="px-3 py-2">
                    <div className="flex gap-1">
                      <button
                        onClick={() => handleToggle(entry)}
                        className={`px-2 py-1 rounded text-[11px] ${entry.isActive ? "bg-amber-100 text-amber-700 hover:bg-amber-200" : "bg-green-100 text-green-700 hover:bg-green-200"}`}
                        aria-label={entry.isActive ? `Deactivate ${entry.ipOrRange}` : `Activate ${entry.ipOrRange}`}
                      >
                        {entry.isActive ? "Deactivate" : "Activate"}
                      </button>
                      <button
                        onClick={() => handleDelete(entry)}
                        className="px-2 py-1 rounded text-[11px] bg-red-100 text-red-700 hover:bg-red-200"
                        aria-label={`Delete ${entry.ipOrRange}`}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showAdd && (
        <AddEntryModal
          onClose={() => setShowAdd(false)}
          onAdded={() => { setShowAdd(false); fetchEntries(); }}
          api={api}
          toastAlert={toastAlert}
        />
      )}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────
// Audit Log
// ─────────────────────────────────────────────────────────────────

function AllowlistAuditLog() {
  const api = useApi();
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const resp = await api.messaging.get("AgentIpAllowlist/audit?pageSize=100");
        if (resp.ok) {
          const data = await resp.json();
          setEntries(Array.isArray(data.entries) ? data.entries : []);
          setTotal(data.total ?? 0);
        }
      } catch { /* graceful */ }
      finally { setLoading(false); }
    })();
  }, [api.messaging]);

  const actionBadge = (action: string) => {
    const colors: Record<string, string> = {
      EntryAdded: "bg-green-100 text-green-700",
      EntryRemoved: "bg-red-100 text-red-700",
      EntryUpdated: "bg-blue-100 text-blue-700",
      RestrictionEnabled: "bg-green-100 text-green-700",
      RestrictionDisabled: "bg-slate-100 text-slate-600",
      LoginBlocked: "bg-red-100 text-red-700",
      LoginAllowed: "bg-green-100 text-green-700",
      EmergencyBypassCreated: "bg-orange-100 text-orange-700"
    };
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${colors[action] ?? "bg-slate-100 text-slate-600"}`}>
        {action}
      </span>
    );
  };

  if (loading) return <div className="text-sm text-slate-400 py-8">Loading audit log...</div>;

  return (
    <div>
      <p className="text-sm text-slate-500 mb-4">{total} audit entr{total !== 1 ? "ies" : "y"} on record.</p>
      {entries.length === 0 ? (
        <div className="text-sm text-slate-400 py-8 text-center">No audit entries yet.</div>
      ) : (
        <div className="overflow-x-auto border border-slate-200 rounded-lg">
          <table className="w-full text-left text-sm">
            <thead className="bg-slate-50 text-xs text-slate-500 uppercase">
              <tr>
                <th className="px-3 py-2">Date/Time</th>
                <th className="px-3 py-2">Action</th>
                <th className="px-3 py-2">Performed By</th>
                <th className="px-3 py-2">IP Address</th>
                <th className="px-3 py-2">Detail</th>
              </tr>
            </thead>
            <tbody>
              {entries.map(entry => (
                <tr key={entry.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-3 py-2 text-xs">{entry.performedAt}</td>
                  <td className="px-3 py-2">{actionBadge(entry.action)}</td>
                  <td className="px-3 py-2 text-xs">{entry.performedBy}</td>
                  <td className="px-3 py-2 text-xs font-mono">{entry.ipAddress}</td>
                  <td className="px-3 py-2 text-xs text-slate-500 max-w-[300px] truncate" title={entry.detail}>{entry.detail}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Add Entry Modal
// ─────────────────────────────────────────────────────────────────

function AddEntryModal({
  onClose,
  onAdded,
  api,
  toastAlert
}: {
  onClose: () => void;
  onAdded: () => void;
  api: ReturnType<typeof useApi>;
  toastAlert: ReturnType<typeof useToastAlert>;
}) {
  const [ipOrRange, setIpOrRange] = useState("");
  const [type, setType] = useState("single");
  const [label, setLabel] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [isEmergencyBypass, setIsEmergencyBypass] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const body: any = {
        ipOrRange: ipOrRange.trim(),
        type,
        label: label.trim() || undefined,
        isEmergencyBypass
      };
      if (expiresAt) body.expiresAt = new Date(expiresAt).toISOString();

      const resp = await api.messaging.post("AgentIpAllowlist", body);
      if (resp.ok) {
        toastAlert.displayToast(Severity.Success, `${isEmergencyBypass ? "Emergency bypass" : "IP"} added to allowlist.`);
        onAdded();
      } else {
        const err = await resp.json();
        toastAlert.displayToast(Severity.Error, err.error ?? "Failed to add entry.");
      }
    } catch {
      toastAlert.displayToast(Severity.Error, "Failed to add entry.");
    } finally {
      setSubmitting(false);
    }
  };

  const placeholder = type === "single" ? "203.0.113.45" : type === "cidr" ? "192.168.1.0/24" : "10.0.0.1-10.0.0.100";

  return (
    <Modal show maxWidth="max-w-md">
      <ModalHeader title="Add Allowed IP / Range" onClose={onClose} />
      <form onSubmit={handleSubmit}>
        <ModalBody>
          <div className="space-y-3">
            <div>
              <label htmlFor="ipType" className="block text-sm font-medium text-slate-700 mb-1">Type</label>
              <select id="ipType" value={type} onChange={e => setType(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm">
                <option value="single">Single IP</option>
                <option value="cidr">CIDR Range (e.g. 192.168.1.0/24)</option>
                <option value="range">IP Range (e.g. 10.0.0.1-10.0.0.100)</option>
              </select>
            </div>
            <div>
              <label htmlFor="ipValue" className="block text-sm font-medium text-slate-700 mb-1">IP / Range</label>
              <input
                id="ipValue" type="text" required autoFocus
                value={ipOrRange} onChange={e => setIpOrRange(e.target.value)}
                placeholder={placeholder}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm font-mono"
              />
            </div>
            <div>
              <label htmlFor="ipLabel" className="block text-sm font-medium text-slate-700 mb-1">Label (optional)</label>
              <input
                id="ipLabel" type="text"
                value={label} onChange={e => setLabel(e.target.value)}
                placeholder="Corporate HQ, VPN pool, Home office..."
                maxLength={200}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
              />
            </div>
            <div>
              <label htmlFor="ipExpires" className="block text-sm font-medium text-slate-700 mb-1">
                Expires (optional — leave empty for permanent)
              </label>
              <input
                id="ipExpires" type="datetime-local"
                value={expiresAt} onChange={e => setExpiresAt(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
              />
            </div>
            <label className="flex items-start gap-3 p-3 border border-orange-200 bg-orange-50 rounded-lg cursor-pointer">
              <input
                type="checkbox"
                checked={isEmergencyBypass}
                onChange={e => setIsEmergencyBypass(e.target.checked)}
                className="mt-0.5"
              />
              <div>
                <span className="text-sm font-medium text-orange-800">Emergency Bypass</span>
                <p className="text-xs text-orange-700 mt-0.5">
                  Mark as a temporary bypass (e.g. travel, remote work). Generates extra audit events.
                  Recommend setting an expiry date.
                </p>
              </div>
            </label>
          </div>
        </ModalBody>
        <ModalFooter>
          <SecondaryBlueButton onClick={onClose} label="Cancel" />
          <PrimaryBlueButton type="submit" label={submitting ? "Adding..." : "Add to Allowlist"} disabled={submitting || !ipOrRange.trim()} />
        </ModalFooter>
      </form>
    </Modal>
  );
}
